<script setup lang="ts">
    import BaseButton from '@/components/BaseButton.vue';
    import { generateImageWithWatermarks } from '@/littleWedding/canvasMaterialsManager';
    import { useConfigStore } from '@/store/config.store';
    import FormatSelection from '@/components/FormatSelection.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { EProductFormat } from '@/typescript/enums/EProductFormat';
    import { Events } from '@/services/Events';
    import { dataStore } from '@/commons/DataStore';
    import { useMaterialUpdater } from '@/composables/useMaterialUpdater';
    import { useField } from '@/composables/useField';
    import { ref, onMounted, onUnmounted } from 'vue';
    import { storeToRefs } from 'pinia';

    defineEmits<{ close: [] }>();
    const { currentSide, isMobile, productFormat } = storeToRefs(useConfigStore());
    const fabricWorldRecto = dataStore.wFabricWorldRecto;
    const fabricWorldVerso = dataStore.wFabricWorldVerso;
    const imgSrc = ref();
    const { filteredFields } = useField();

    async function updateCallback() {
        updateImage(currentSide.value);
    }

    onMounted(async () => {
        Events.on('rendered-onAfter', updateCallback);
        updateImage(currentSide.value);
    });

    async function updateImage(format: 'recto' | 'verso') {
        const blob =
            format === 'recto' ? await generateImageWithWatermarks(fabricWorldRecto) : await generateImageWithWatermarks(fabricWorldVerso);
        if (blob) {
            imgSrc.value = URL.createObjectURL(blob);
        }
    }

    function sideChanged() {
        const { updateMaterial } = useMaterialUpdater(filteredFields, fabricWorldRecto);
        updateMaterial(true);

        updateImage(currentSide.value);
    }

    onUnmounted(() => {
        Events.off('rendered-onAfter', updateCallback);
    });
</script>
<template>
    <div
        class="fixed z-40 flex h-screen w-screen items-center justify-center bg-bg-primary/30"
        tabindex="0"
        role="button"
        aria-label="Close modal"
        @click.self="$emit('close')"
        @keydown.self.escape="$emit('close')"
    >
        <div
            :class="[
                !isMobile && 'h-5/6 w-2/3',
                isMobile && 'justify-center',
                'relative flex h-[80vh] max-h-screen flex-col items-center gap-5 bg-transparent p-6',
            ]"
            role="button"
            tabindex="0"
            @click.self="$emit('close')"
            @keydown.self.escape="$emit('close')"
        >
            <div
                :class="[
                    !isMobile && 'min-h-0 w-fit',
                    isMobile && 'w-full',
                    'flex flex-col items-center border border-dashed border-primary bg-white p-0 leading-4',
                ]"
            >
                <img
                    :src="imgSrc"
                    :alt="$t('bat_preview')"
                    :class="[!isMobile && 'h-full', isMobile && 'w-full', 'max-h-full max-w-full object-contain']"
                />
            </div>

            <FormatSelection
                v-if="productFormat === EProductFormat.RECTO_VERSO"
                class="relative !right-0 !mr-0"
                @side-changed="sideChanged"
            />
        </div>
        <BaseButton
            :class="['absolute', !isMobile && 'right-8 top-8 h-8 w-8', isMobile && 'right-5 top-7 h-16 w-16']"
            @click="$emit('close')"
        >
            <span class="sr-only">
                {{ $t('close_popup') }}
            </span>
            <BaseIcon
                class="h-5 w-5"
                name="XMarkIcon"
            />
        </BaseButton>
    </div>
</template>
